import React, { useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from './domain/auth/useAuth';
import { UserType } from './domain/user/interface';

interface IAppContextProps {
    isFiltersSidebarEnabled: boolean;
}

export const AppContext = React.createContext<IAppContextProps>({} as IAppContextProps);

// Context for general application state
export const AppContextProvider = ({ children }: { children: React.ReactNode }) => {
    const auth = useAuth();
    const location = useLocation();
    const user = auth?.user || null;

    const isFiltersSidebarEnabled = useMemo(() => {
        if (!user) return false;

        const allowedAccessRoles = FILTERS_SIDEBAR_ACCESS[location.pathname];
        if (!allowedAccessRoles) return false;

        return allowedAccessRoles.includes(user.user_type);
    }, [location.pathname, user]);

    return <AppContext.Provider value={{ isFiltersSidebarEnabled }}>{children}</AppContext.Provider>;
};

const ALL_USER_TYPES = Object.values(UserType);
const ADMIN_USER_TYPES = [UserType.ADMIN, UserType.ACCOUNT_MANAGER];

const FILTERS_SIDEBAR_ACCESS: Record<string, UserType[]> = {
    '/sites': ALL_USER_TYPES,
    '/nominations': ALL_USER_TYPES,
    '/nomination-readiness-tracking': ALL_USER_TYPES,
    '/energy-usage-data/analytics': ALL_USER_TYPES,
    '/settlement-data': ALL_USER_TYPES,
    '/events/tracking': ALL_USER_TYPES,
    '/reporting/customer-utility-bills': ADMIN_USER_TYPES,
    '/reporting/customer-utility-bills/files': ADMIN_USER_TYPES,

    '/events/company-participation': ADMIN_USER_TYPES,
    '/event-analytics/performance': ADMIN_USER_TYPES,
    '/admin/agreements': ADMIN_USER_TYPES,
    '/ven-devices/customer-ven': ADMIN_USER_TYPES,
    '/ven-devices/cloud-ven': ADMIN_USER_TYPES,

    '/pjm/locations': ADMIN_USER_TYPES,
    '/pjm/registrations': ADMIN_USER_TYPES,
    '/pjm/polling-status': ADMIN_USER_TYPES,
};

export const useAppContext = () => {
    return useContext<IAppContextProps>(AppContext);
};
